import { useState } from "react";
import { Camera, Pencil, Mic, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const gradientStyles = {
  '--color-1': 'hsl(0 100% 50%)',
  '--color-2': 'hsl(270 100% 50%)',
} as React.CSSProperties;

export const EntrySubmission = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [showTextBox, setShowTextBox] = useState(false);
  const [entryText, setEntryText] = useState("");

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length === 0) {
      toast.error("Please upload image files only");
      return;
    }

    setIsUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const file = validFiles[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('food_images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { error: dbError, data: imageData } = await supabase
        .from('food_images')
        .insert({
          file_path: fileName,
          user_id: user.id
        })
        .select()
        .single();

      if (dbError) throw dbError;

      const { data: { signedUrl } } = await supabase.storage
        .from('food_images')
        .createSignedUrl(fileName, 3600);

      if (!signedUrl) throw new Error("Failed to get signed URL");

      const { error: processError } = await supabase.functions
        .invoke('process-food-image', {
          body: {
            image_id: imageData.id,
            image_url: signedUrl,
            user_id: user.id
          }
        });

      if (processError) throw processError;

      toast.success("Photo uploaded successfully!");
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Failed to upload photo");
    } finally {
      setIsUploading(false);
    }
  };

  const handleTextSubmit = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data: entryData, error } = await supabase
        .from('food_entries')
        .insert({
          user_id: user.id,
          notes: entryText,
          analysis_status: 'pending',
          consumed_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      const { error: processError } = await supabase.functions
        .invoke('process-entry-text', {
          body: {
            entry_id: entryData.id,
            user_id: user.id
          }
        });

      if (processError) throw processError;

      // Put these before the response comes back
      toast.success("Entry submitted successfully!");
      setShowTextBox(false);
      setEntryText("");
    } catch (error) {
      console.error('Submit error:', error);
      toast.error("Failed to submit entry");
    }
  };

  return (
    <div className="max-w-sm mx-auto p-2 relative">
      <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-1)] to-[var(--color-2)] animate-gradient-border rounded-[64px] blur-[32px] opacity-50" />
      <Card 
        className="relative bg-white shadow-lg"
        style={gradientStyles}
      >
        <CardContent className="p-6">
          <div className="flex justify-center gap-6">
            <Input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileInput}
              id="photo-upload"
              disabled={isUploading}
            />
            <Button
              variant="default"
              size="lg"
              asChild
              disabled={isUploading}
              onClick={() => setShowTextBox(false)}
              className={`w-16 h-16 rounded-2xl bg-[#0F172A] hover:bg-[#1E293B] ${isUploading ? "opacity-50" : ""}`}
            >
              <label htmlFor="photo-upload">
                <Camera className="w-10 h-10" />
              </label>
            </Button>

            <Button
              variant="default"
              size="lg"
              onClick={() => {
                setShowTextBox(true);
                setTimeout(() => {
                  document.querySelector('textarea')?.focus();
                }, 0);
              }}
              className="w-16 h-16 rounded-2xl bg-[#0F172A] hover:bg-[#1E293B]"
            >
              <Pencil className="w-10 h-10" />
            </Button>

            <Button
              variant="default"
              size="lg"
              onClick={() => {
                setShowTextBox(false);
                toast.info("Audio entry coming soon!");
              }}
              className="w-16 h-16 rounded-2xl bg-[#0F172A] hover:bg-[#1E293B]"
            >
              <Mic className="w-10 h-10" />
            </Button>
          </div>

          {showTextBox && (
            <div className="grid w-full gap-2 mt-6">
              <Textarea 
                placeholder="Write about what you ate..."
                className="min-h-[120px] rounded-xl"
                value={entryText}
                onChange={(e) => setEntryText(e.target.value)}
              />
              <Button
                onClick={handleTextSubmit}   
                className="w-full rounded-xl mt-4"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};